import React, { type ReactNode } from 'react';
import memoizeOne from 'memoize-one';
import { ff } from '@atlassian/jira-feature-flagging';
import type { IntlFormatV2 as IntlFormat } from '@atlassian/jira-intl';
import {
	DEFAULT_COLUMN_WIDTH,
	COLUMN_WIDTH_TEXT_LONG,
	COLUMN_WIDTH_TEXT_SHORT,
	COLUMN_WIDTH_TEXT_SUMMARY,
	KNOWN_COLUMN_TYPES,
} from '../common/constants';
import type { ColumnConfig, KnownColumnType } from '../common/types';
import messages from './messages';

type ColumnConfigGetter = () => ColumnConfig;
type ColumnConfigMap = Record<KnownColumnType, ColumnConfigGetter>;

export const getColumnConfig = memoizeOne(
	(formatMessage: IntlFormat['formatMessage']): ColumnConfigMap => ({
		[KNOWN_COLUMN_TYPES.ASSIGNEE]: () => ({
			defaultWidth: DEFAULT_COLUMN_WIDTH,
			width: DEFAULT_COLUMN_WIDTH, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
		}),
		[KNOWN_COLUMN_TYPES.CREATED]: () => ({
			defaultWidth: 126,
			width: ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') ? 126 : 106, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.oldestToNewestOrder),
			descText: formatMessage(messages.newestToOldestOrder),
		}),
		[KNOWN_COLUMN_TYPES.UPDATED]: () => ({
			defaultWidth: 130,
			width: ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') ? 130 : 106, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.oldestToNewestOrder),
			descText: formatMessage(messages.newestToOldestOrder),
		}),
		[KNOWN_COLUMN_TYPES.DUE_DATE]: () => ({
			defaultWidth: 140,
			// remove width with nin-resizable-columns_8z4eo
			width: 134,
			ascText: formatMessage(messages.oldestToNewestOrder),
			descText: formatMessage(messages.newestToOldestOrder),
		}),
		[KNOWN_COLUMN_TYPES.RESOLUTION_DATE]: () => ({
			defaultWidth: 106,
			width: 106, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.oldestToNewestOrder),
			descText: formatMessage(messages.newestToOldestOrder),
		}),
		[KNOWN_COLUMN_TYPES.PRIORITY]: () => ({
			defaultWidth: 140,
			width: 140, // remove width with nin-resizable-columns_8z4eo
			shouldResizeWithTable: false,
		}),
		[KNOWN_COLUMN_TYPES.ISSUE_KEY]: () => ({
			defaultWidth: 96,
			width: 96, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
		}),
		[KNOWN_COLUMN_TYPES.ISSUE_OPERATIONS]: () => ({
			title: '',
			defaultWidth: 48,
			width: 48, // remove width with nin-resizable-columns_8z4eo
			isSortable: false,
			shouldResizeWithTable: ff('jsv-one-issue-table-spreadsheet-ui_ygrnf'),
		}),
		[KNOWN_COLUMN_TYPES.STATUS]: () => ({
			defaultWidth: 140,
			width: 140, // remove width with nin-resizable-columns_8z4eo
		}),
		[KNOWN_COLUMN_TYPES.SUMMARY]: () => ({
			defaultWidth: COLUMN_WIDTH_TEXT_SUMMARY,
			width: COLUMN_WIDTH_TEXT_SUMMARY, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
		}),
		[KNOWN_COLUMN_TYPES.ISSUE_TYPE]: () => ({
			// Overrides title from the server's displayName from "Issue Type" to "Type" as per designs
			title: formatMessage(messages.columnHeaderIssueType),
			defaultWidth: 101,
			width: ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') ? 101 : 66, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
			shouldResizeWithTable: false,
		}),
		[KNOWN_COLUMN_TYPES.RESOLUTION]: () => ({
			defaultWidth: 106,
			width: 106, // remove width with nin-resizable-columns_8z4eo
		}),
		[KNOWN_COLUMN_TYPES.REPORTER]: () => ({
			defaultWidth: DEFAULT_COLUMN_WIDTH,
			width: DEFAULT_COLUMN_WIDTH, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
		}),
		[KNOWN_COLUMN_TYPES.CREATOR]: () => ({
			defaultWidth: DEFAULT_COLUMN_WIDTH,
			width: DEFAULT_COLUMN_WIDTH, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
		}),
		[KNOWN_COLUMN_TYPES.SPRINT]: () => ({
			defaultWidth: DEFAULT_COLUMN_WIDTH,
			width: DEFAULT_COLUMN_WIDTH, // remove width with nin-resizable-columns_8z4eo
		}),
		[KNOWN_COLUMN_TYPES.PARENT]: () => ({
			defaultWidth: COLUMN_WIDTH_TEXT_SUMMARY,
			width: COLUMN_WIDTH_TEXT_SUMMARY, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
		}),
		// Labels renders using an Atlaskit Tag which doesn't respect its container, and there's no prop to override it...
		// <Tag/> has a maxWidth of 180px with 4px of padding either side
		// And we need to accound for 5px & 8px for left and right padding on our cell respectively
		[KNOWN_COLUMN_TYPES.LABELS]: () => ({
			defaultWidth: 201,
			width: 201, // remove width with nin-resizable-columns_8z4eo
			shouldResizeWithTable: false,
		}),
		[KNOWN_COLUMN_TYPES.COMPONENTS]: () => ({
			defaultWidth: DEFAULT_COLUMN_WIDTH,
			width: DEFAULT_COLUMN_WIDTH, // remove width with nin-resizable-columns_8z4eo
		}),
		[KNOWN_COLUMN_TYPES.FIX_VERSIONS]: () => ({
			defaultWidth: DEFAULT_COLUMN_WIDTH,
			width: DEFAULT_COLUMN_WIDTH, // remove width with nin-resizable-columns_8z4eo
		}),
		[KNOWN_COLUMN_TYPES.STATUS_CATEGORY]: () => ({
			defaultWidth: 140,
			width: 140, // remove width with nin-resizable-columns_8z4eo
		}),
		[KNOWN_COLUMN_TYPES.PROJECT]: () => ({
			defaultWidth: DEFAULT_COLUMN_WIDTH,
			width: DEFAULT_COLUMN_WIDTH, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
		}),
		[KNOWN_COLUMN_TYPES.EPIC_LINK]: () => ({
			// EpicLink renders using an Atlaskit Tag which doesn't respect its container, and there's no prop to override it...
			// <Tag/> has a maxWidth of 180px with 4px of padding either side
			// And we need to account for 5px & 8px for left and right padding on our cell respectively
			defaultWidth: 201,
			width: 201, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
			...(ff('com.atlassian.rm.jpo.deprecate-epic-link-nin') && {
				warningText: formatMessage(messages.deprecatedEpicLinkMessage, {
					strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
				}),
			}),
		}),
		[KNOWN_COLUMN_TYPES.DESCRIPTION]: () => ({
			defaultWidth: COLUMN_WIDTH_TEXT_LONG,
			width: COLUMN_WIDTH_TEXT_LONG, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
		}),
		[KNOWN_COLUMN_TYPES.ENVIRONMENT]: () => ({
			defaultWidth: COLUMN_WIDTH_TEXT_LONG,
			width: COLUMN_WIDTH_TEXT_LONG, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
		}),
		[KNOWN_COLUMN_TYPES.TEXT_AREA]: () => ({
			defaultWidth: COLUMN_WIDTH_TEXT_LONG,
			width: COLUMN_WIDTH_TEXT_LONG, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
		}),
		[KNOWN_COLUMN_TYPES.TEXT]: () => ({
			defaultWidth: COLUMN_WIDTH_TEXT_SHORT,
			width: COLUMN_WIDTH_TEXT_SHORT, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
		}),
		[KNOWN_COLUMN_TYPES.READ_ONLY]: () => ({
			defaultWidth: COLUMN_WIDTH_TEXT_SHORT,
			width: COLUMN_WIDTH_TEXT_SHORT, // remove width with nin-resizable-columns_8z4eo
			ascText: formatMessage(messages.aToZOrder),
			descText: formatMessage(messages.zToAOrder),
		}),
		[KNOWN_COLUMN_TYPES.AFFECTS_VERSIONS]: () => ({
			defaultWidth: DEFAULT_COLUMN_WIDTH,
			width: DEFAULT_COLUMN_WIDTH, // remove width with nin-resizable-columns_8z4eo
		}),
		[KNOWN_COLUMN_TYPES.REQUEST_TYPE]: () => ({
			defaultWidth: 251,
			width: 251, // remove width with nin-resizable-columns_8z4eo
			isSortable: false,
		}),
	}),
);
